/**
 * User hostility lexicon loader — tiers A (provoc) / B (abuse).
 * Data: onyxHostilityLexicon.json (no language hardcoding in call sites).
 */
import lexicon from './onyxHostilityLexicon.json';

export type OnyxHostilityTier = 'A' | 'B' | 'none';

export type OnyxHostilityResult = {
  tier: OnyxHostilityTier;
  family?: string;
};

type LexiconEntry = {
  tier: 'A' | 'B';
  family: string;
  patterns: string[];
};

type HostilityLexicon = {
  neverMatchAlone: string[];
  entries: LexiconEntry[];
  challengePatterns: string[];
  safetyPatterns: string[];
};

const data = lexicon as HostilityLexicon;

function normalize(text: string): string {
  return text.normalize('NFKC').toLowerCase().trim();
}

function compilePattern(raw: string): RegExp {
  // If author already used regex metacharacters intentionally, keep as-is with i flag.
  if (/\\[bBdDsSwW]|[\[\]()|?]/.test(raw)) {
    return new RegExp(raw, 'iu');
  }
  const escaped = raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(escaped, 'iu');
}

const compiledEntries = data.entries.map((e) => ({
  tier: e.tier,
  family: e.family,
  regexes: e.patterns.map(compilePattern),
}));

const challengeRegexes = data.challengePatterns.map(compilePattern);
const safetyRegexes = data.safetyPatterns.map(compilePattern);
const neverAlone = new Set(data.neverMatchAlone.map((s) => normalize(s)));

/**
 * Classify current user message only (no history grudge).
 * B wins over A.
 */
export function detectHostilityTier(message: string): OnyxHostilityResult {
  const text = normalize(message);
  if (!text) return { tier: 'none' };

  // Whole message is only a resto allowlist token → never hostility
  if (neverAlone.has(text)) return { tier: 'none' };

  let foundA: OnyxHostilityResult | null = null;

  for (const entry of compiledEntries) {
    for (const re of entry.regexes) {
      if (!re.test(text)) continue;
      // Skip if the only match is a neverMatchAlone token (single-word messages already handled)
      if (entry.tier === 'B') {
        return { tier: 'B', family: entry.family };
      }
      if (!foundA) foundA = { tier: 'A', family: entry.family };
    }
  }

  return foundA || { tier: 'none' };
}

export function detectOnyxChallenge(message: string): boolean {
  const text = normalize(message);
  if (!text) return false;
  return challengeRegexes.some((re) => re.test(text));
}

export function detectOnyxSafetyTopic(message: string): boolean {
  const text = normalize(message);
  if (!text) return false;
  return safetyRegexes.some((re) => re.test(text));
}

export function hostilityCueForPrompt(tier: OnyxHostilityTier): string {
  if (tier === 'B') return '[HOSTILITY: B]';
  if (tier === 'A') return '[HOSTILITY: A]';
  return '[HOSTILITY: none]';
}
